import { faPlus, faTrashCan, faListUl, faCircleCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ProjectsTopSection from '../../components/project-management/ProjectsTopSection';
import CardViewItem from '../../components/project-management/CardViewItem';
import AdvanceTableFooter from '../../components/base/AdvanceTableFooter';
import useAdvanceTable from '../../hooks/useAdvanceTable';
import AdvanceTableProvider from '../../providers/AdvanceTableProvider';
import { Col, Row, Nav } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useJobs } from '../../features/jobs/hooks/useJobs';
import { useArchivedJobs } from '../../features/jobs/hooks/useArchivedJobs';
import { adaptJobToProjectRow } from '../../helpers/jobsAdapter';
import { useMemo, useState } from 'react';
import CompletedProjectsPanel from '../../components/project-management/CompletedProjectsPanel';
import { Bars } from 'react-loader-spinner';
import { jobSearchKey, matchesSearch } from '../../helpers/cableSearch';

export const columns = [
  {
    // For filtering and searching projects by status
    id: 'status',
    accessorFn: ({ status }) => status.label
  },
  {
    // For searching projects by name
    accessorKey: 'name'
  }
];

const ProjectCardView = () => {
  const [view, setView] = useState('active'); // 'active' | 'deleted'
  const { data: jobs = [], isLoading } = useJobs();
  const { data: archived = [] } = useArchivedJobs(view === 'deleted');

  const source = view === 'deleted' ? archived : jobs;
  const tableData = useMemo(() => {
    return source.map(j => ({
      ...adaptJobToProjectRow(j),
      _deleted: view === 'deleted'
    }));
  }, [source, view]);

  const table = useAdvanceTable({
    data: tableData,
    columns,
    // 12 fills three rows of the xxl grid. It was 10 with no footer rendered,
    // so 10 of 28 products were the only ones that existed as far as this page
    // was concerned — the other 18 were paginated away behind nothing.
    pageSize: 12,
    pagination: true,
    sortable: true,
    // Same search rule as the Projects table on Project Monitor — the search box
    // is shared, so it must answer the same in every view.
    globalFilterFn: (row, _columnId, value) =>
      matchesSearch(jobSearchKey(row.original), value)
  });

  // The spinner was built and then dropped on the floor — an expression
  // statement, not a return — so the page rendered an empty grid while loading.
  if (isLoading && !tableData.length)
    return (
      <Bars
        height="80"
        width="80"
        radius="9"
        color={'#3874ff'}
        ariaLabel="three-dots-loading"
        wrapperStyle={styles.spinnerContainer}
      />
    );

  return (
    <div>
      <AdvanceTableProvider {...table}>
        <div className="d-flex flex-wrap mb-4 gap-3 gap-sm-6 align-items-center">
          <h2 className="mb-0">
            <span className="me-3">
              {view === 'deleted' ? 'Deleted projects' : 'Projects'}
            </span>{' '}
            <span className="fw-normal text-700">({tableData.length})</span>
          </h2>
          <Link
            className="btn btn-primary px-5"
            to="/apps/project-management/create-new"
          >
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Add new project
          </Link>
        </div>
        <Nav variant="pills" className="mb-3 gap-2">
          <Nav.Item>
            <Nav.Link
              active={view === 'active'}
              onClick={() => setView('active')}
              style={{ cursor: 'pointer' }}
            >
              <FontAwesomeIcon icon={faListUl} className="me-2" />
              Active
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link
              active={view === 'completed'}
              onClick={() => setView('completed')}
              style={{ cursor: 'pointer' }}
            >
              <FontAwesomeIcon icon={faCircleCheck} className="me-2" />
              Completed
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link
              active={view === 'deleted'}
              onClick={() => setView('deleted')}
              style={{ cursor: 'pointer' }}
            >
              <FontAwesomeIcon icon={faTrashCan} className="me-2" />
              Deleted
            </Nav.Link>
          </Nav.Item>
        </Nav>
        {view === 'completed' ? (
          <CompletedProjectsPanel />
        ) : (
          <>
            {view === 'active' && <ProjectsTopSection activeView="card" />}
            <Row className="g-3">
              {table
                .getRowModel()
                .rows.map(row => row.original)
                .map(project => (
                  // `project.id` does not exist on these rows — every card
                  // carried key={undefined}, so React could not tell them apart
                  // and a filter that reordered them re-mounted the lot.
                  <Col xs={12} sm={6} xl={4} xxl={3} key={project.jobId}>
                    <CardViewItem project={project} />
                  </Col>
                ))}
            </Row>
            {!table.getRowModel().rows.length && (
              <div className="text-center text-700 fs-9 py-5">
                No products match this search.
              </div>
            )}
            <AdvanceTableFooter pagination className="py-3 mb-9" />
          </>
        )}
      </AdvanceTableProvider>
    </div>
  );
};

const styles = {
  spinnerContainer: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    // backgroundColor: 'rgba(0, 0, 0, 0.5)', // Optional: Add a background with some opacity
    zIndex: 1000 // Ensure the spinner is on top
    // backgroundColor: '#000', // Optional: background color
  }
};

export default ProjectCardView;
